// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable } from '@angular/core';
import { LoggedUserFacadeService } from '../state/logged-user/logged-user.facade';
import { LoggedUserResponse, UserRole } from '../state/logged-user';
import { map } from 'rxjs/operators';

/**
 * Allow to restrict user access base on Role's name
 *
 * This Service requires to implement an High Order Resctriction base on a route resolver or a Directive wrapper
 *
 * To Use:
 * To use areRolesAllowed() Method you need to load this component after LoggedUser$ has a value
 *
 * For that you have 2 options:
 *
 * (1) At Route Level
 *
 * Restrict to load a component using LoggedUserResolverService in the route
 * e.g
 *
 * {
    path: 'groups/add-group',
    component: GroupCreateComponent,
    resolve: {
      currentUser: LoggedUserResolverService
    }
  },
 *
 *
 * Then Inside the GroupCreateComponent Component
 *
 * export class GroupCreateComponent {
 * ...
 *
 * _isUserAllowed = this.loggedUserService.areRolesAllowed(['lf-admin', 'super-admin']);

  constructor(
    private loggedUserService: LoggedUserService
  ) { }

  .....

  *
  *(2) At Component Level
  *
  * Restrict to load a component using a directive *loadAfterUser
  *
  * e.g.
  * <div class="col  p-0">
        <app-role-users-table *loadAfterUser
        [roleId]="roleId"
        [users]="(users$ | async)">
    </app-role-users-table>
    </div>
 *
 * then Inside the component
 *
 * export class RoleUsersTableComponent  {

  ...

  _isUserAllowed = this.loggedUserService.areRolesAllowed(['project-admin', 'member-admin']);

  constructor(
    private loggedUserService: LoggedUserService) {}

    ...
 *
 */

@Injectable({ providedIn: 'root' })
export class LoggedUserService {
  loggedUser: LoggedUserResponse | any;
  userRoles: string[] | any = [];
  loggedUser$: any;
  loggedUserLoaded$: any;
  _isServiceLoaded = false;

  constructor(private loggedUserFacade: LoggedUserFacadeService) {
    this.loggedUser$ = this.loggedUserFacade.loggedUser$;
    this.loggedUserLoaded$ = this.loggedUserFacade.loggedUser$.pipe(map(() => true));

    this.loggedUser$.subscribe((loggedUser: LoggedUserResponse) => {
      this.onLoggedUserLoaded(loggedUser);
    });
  }

  onLoggedUserLoaded(loggedUser: LoggedUserResponse) {
    this._isServiceLoaded = true;
    this.loggedUser = loggedUser;
    this.userRoles =
      (this.loggedUser &&
        this.loggedUser.roles &&
        this.loggedUser.roles.map((role: UserRole) => role.role_name.toLowerCase())) ||
      [];
  }

  areRolesAllowed(roles: string[]) {
    if (!this._isServiceLoaded) {
      return false;
    }
    return roles.some(role => this.userRoles.includes(role.toLowerCase()));
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/logged-user.service.ts